import React, { useState, useEffect, useCallback } from "react";
import { Select, DatePicker, Button, Table, Modal, Form, Input, Empty, message, Tooltip } from "antd";
import { PlusOutlined, EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  getOrgList,
  getFacilitiesByOrgId,
  getKpiReport,
  addKpiReport,
  deleteKpiReport,
  getPreSignedUrl,
  getKpiTemplatesByFacility,
  getKpiTemplateFields,
} from "./educationApi";
import { frequencyLabel } from "./KpiSetup";

const { Option } = Select;
const { confirm } = Modal;

const listOf = ({ error, result }) => (!error && result?.success !== false && Array.isArray(result?.result) ? result.result : []);

// What a report for this month covers, by the template's frequency (the ERP
// works it out the same way when saving: utils/kpiPeriod.js).
const periodHint = (frequency, month) => {
  if (!month) return null;
  if (frequency === "weekly") {
    const reference = moment().isSame(month, "month") ? moment() : month.clone().endOf("month");
    const monday = reference.clone().startOf("isoWeek");
    return `Week ${reference.isoWeek()} (${monday.format("DD MMM")} - ${monday.clone().add(6, "days").format("DD MMM YYYY")})`;
  }
  if (frequency === "quarterly") return `Q${month.quarter()} ${month.year()} (${month.clone().startOf("quarter").format("MMM")} - ${month.clone().endOf("quarter").format("MMM")})`;
  return month.format("MMMM YYYY");
};

// Rebuilt against a direct re-read of kpi/index.js + createNewKpiReport.js.
// Known gap: the old screen's Email popover action isn't wired up here.
// The facility reports on one of its group's KPI templates.
const CreateKpiReportModal = ({ visible, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [orgs, setOrgs] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [templates, setTemplates] = useState([]); // the facility's group's templates
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible) return;
    form.resetFields();
    setFields([]);
    setTemplates([]);
    (async () => {
      const { error, result } = await getOrgList();
      setOrgs(!error && Array.isArray(result?.result) ? result.result : []);
    })();
  }, [visible, form]);

  const onTemplateChange = useCallback(async (templateId) => {
    setFields([]);
    if (templateId === undefined) return;
    const rows = listOf(await getKpiTemplateFields(templateId));
    setFields(rows.map((f) => ({ ...f, actual: "", result: "100%" })));
  }, [form]);

  const onOrgChange = useCallback(async (orgId) => {
    form.setFieldsValue({ facility_name: undefined, kpi_template: undefined });
    setFacilities([]);
    setTemplates([]);
    setFields([]);
    const facRes = await getFacilitiesByOrgId(orgId);
    setFacilities(!facRes.error && Array.isArray(facRes.result?.result) ? facRes.result.result : []);
  }, [form]);

  const onFacilityChange = useCallback(async (facilityId) => {
    const list = listOf(await getKpiTemplatesByFacility(facilityId));
    setTemplates(list);
    const first = list[0]?.kpi_template_id;
    form.setFieldsValue({ kpi_template: first });
    onTemplateChange(first);
  }, [form, onTemplateChange]);

  const updateField = (index, key, value) => {
    setFields((prev) => prev.map((f, i) => (i === index ? { ...f, [key]: value } : f)));
  };

  const onResultChange = (index, raw) => {
    let value = raw.replace("%", "");
    if (value === "" || /^\d{1,3}$/.test(value)) {
      const num = Math.min(Number(value || 0), 100);
      updateField(index, "result", `${value === "" ? "" : num}%`);
    }
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    if (!fields.length) return message.error("This KPI template has no KPI rows yet. Add them in KPI Setup.");
    setSaving(true);
    try {
      const body = {
        month: values.month.format("MMMM"),
        year: Number(values.month.format("YYYY")),
        facility_id: values.facility_name,
        kpi_report_pdf: fields,
      };
      body.kpi_template_id = values.kpi_template;
      const { error, result } = await addKpiReport(body);
      if (error || result?.success === false) message.error(result?.message || "Failed to save.");
      else { message.success("KPI report added."); onSaved(); onClose(); }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="New Report" visible={visible} onCancel={onClose} onOk={handleOk} confirmLoading={saving} destroyOnClose width={700}>
      <Form form={form} layout="vertical">
        <Form.Item name="org_name" label="Group" rules={[{ required: true, message: "Please select a group." }]}>
          <Select showSearch optionFilterProp="children" onChange={onOrgChange}>
            {orgs.map((o) => <Option key={o.org_id ?? o.id} value={o.org_id ?? o.id}>{o.org_name ?? o.name}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="facility_name" label="Facility" rules={[{ required: true, message: "Please select a facility." }]}>
          <Select showSearch optionFilterProp="children" disabled={!facilities.length} onChange={onFacilityChange}>
            {facilities.map((f) => <Option key={f.facility_id ?? f.id} value={f.facility_id ?? f.id}>{f.facility_name ?? f.name}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="kpi_template" label="KPI Template" rules={[{ required: true, message: "Please select a KPI template." }]}>
          <Select onChange={onTemplateChange} notFoundContent="No KPI templates for this group. Add one in KPI Setup.">
            {templates.map((t) => (
              <Option key={t.kpi_template_id} value={t.kpi_template_id}>
                {t.name} ({frequencyLabel(t.frequency)})
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="month" label="Month" rules={[{ required: true, message: "Please select a month." }]}>
          <DatePicker picker="month" format="MMM YYYY" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(prev, next) => prev.kpi_template !== next.kpi_template || prev.month !== next.month}>
          {({ getFieldValue }) => {
            const templateId = getFieldValue("kpi_template");
            if (templateId === undefined) return null;
            const template = templates.find((t) => t.kpi_template_id === templateId);
            const frequency = template ? template.frequency : "monthly";
            const hint = periodHint(frequency, getFieldValue("month"));
            return (
              <div style={{ fontSize: 13, color: "#5B6472", marginTop: -8, marginBottom: 16 }}>
                Frequency: <b>{frequencyLabel(frequency)}</b>
                {hint ? <> · This report covers <b>{hint}</b>. A second report for the same template and period replaces it.</> : null}
              </div>
            );
          }}
        </Form.Item>
      </Form>
      {fields.length > 0 && (
        <Table
          rowKey={(r, i) => i}
          size="small"
          pagination={false}
          scroll={{ y: 400 }}
          dataSource={fields}
          columns={[
            { title: "Description", dataIndex: "measure_title", key: "measure_title" },
            { title: "Measure", dataIndex: "measure_data", key: "measure_data" },
            {
              title: "Actual",
              key: "actual",
              render: (_, row, i) => <Input.TextArea rows={2} value={row.actual} onChange={(e) => updateField(i, "actual", e.target.value)} />,
            },
            { title: "Target", dataIndex: "target", key: "target" },
            {
              title: "Result",
              key: "result",
              render: (_, row, i) => <Input value={row.result} onChange={(e) => onResultChange(i, e.target.value)} style={{ width: 80 }} />,
            },
          ]}
        />
      )}
    </Modal>
  );
};

const Kpi = () => {
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState(null);
  const [month, setMonth] = useState(undefined);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    (async () => {
      const { error, result } = await getOrgList();
      setOrgs(!error && Array.isArray(result?.result) ? result.result : []);
    })();
  }, []);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    const body = { org_id: orgId };
    if (month) body.kpi_inspection_date = month.format("MM-YYYY");
    const { error, result } = await getKpiReport(body);
    setRows(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, [orgId, month]);

  useEffect(() => { load(); }, [load]);

  const handleView = async (row) => {
    const { error, result } = await getPreSignedUrl(row.kpi_inspection_pdf_link);
    if (!error && result?.result) window.open(result.result, "_blank");
    else message.error("Could not open the report.");
  };

  const handleDelete = (row) => {
    confirm({
      title: "Delete KPI report",
      content: "Delete this KPI report?",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const { error, result } = await deleteKpiReport(row.id);
        if (error) message.error(result?.message || "Failed to delete.");
        else { message.success("Deleted."); load(); }
      },
    });
  };

  const columns = [
    { title: "Facility", dataIndex: "facility_name", key: "facility_name" },
    { title: "Template", dataIndex: "template_name", key: "template_name", render: (v) => v || "Standard KPI" },
    {
      // weekly / quarterly reports are named after their period ("Week 44 2026", "Q4 2026")
      title: "Period",
      dataIndex: "kpi_inspection_date",
      key: "kpi_inspection_date",
      render: (v, row) => row.kpi_name || (v ? moment(v, "MM-YYYY").format("MMM YYYY") : "-"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, row) => (
        <>
          <Tooltip title="View"><Button size="small" icon={<EyeOutlined />} onClick={() => handleView(row)} style={{ marginRight: 8 }} aria-label="View" /></Tooltip>
          <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} aria-label="Delete" /></Tooltip>
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Group</div>
          <Select showSearch optionFilterProp="children" placeholder="Select group" style={{ width: 220 }} value={orgId} onChange={setOrgId} allowClear={false}>
            {orgs.map((o) => <Option key={o.org_id ?? o.id} value={o.org_id ?? o.id}>{o.org_name ?? o.name}</Option>)}
          </Select>
        </div>
        <div>
          <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Month (Optional)</div>
          <DatePicker picker="month" format="MMM YYYY" value={month} onChange={setMonth} />
        </div>
        <Button icon={<PlusOutlined />} type="primary" shape="round" onClick={() => setModalVisible(true)}>New Report</Button>
      </div>

      {!orgId ? (
        <Empty description="Select a group to view KPI reports." />
      ) : rows.length === 0 && !loading ? (
        <Empty description="No KPI reports for these filters." />
      ) : (
        <Table rowKey={(r, i) => r.id ?? i} columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 20 }} />
      )}

      <CreateKpiReportModal visible={modalVisible} onClose={() => setModalVisible(false)} onSaved={load} />
    </div>
  );
};

export default Kpi;
